import { useRef, useState } from 'react';
import { clientService } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X } from 'lucide-react';

interface CreatedClient {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  assigned_agent_id: string | null;
}

interface Props {
  onClose: () => void;
  onCreated: (client: CreatedClient) => void;
}

const EMPTY_FORM = { name: '', email: '', password: '', phone: '' };

export function CreateClientModal({ onClose, onCreated }: Props) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Ingresa el nombre del cliente.');
      return;
    }
    if (!form.email.trim()) {
      setError('Ingresa el correo del cliente.');
      return;
    }
    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      const client = await clientService.create({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
      });
      onCreated(client);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg ?? 'No se pudo crear el cliente. Verifica los datos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (!submitting && e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-background border border-border rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
          <h2 className="font-semibold text-base">Nuevo cliente</h2>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="Cerrar" className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Nombre completo</Label>
            <Input
              id="c-name"
              ref={firstInputRef}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="María García"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="c-email">Correo electrónico</Label>
            <Input
              id="c-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="maria@ejemplo.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="c-password">Contraseña de acceso</Label>
            <Input
              id="c-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="Mínimo 6 caracteres"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="c-phone">
              Teléfono <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="c-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+52 55 0000 0000"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={submitting}>
              {submitting ? 'Creando…' : 'Crear cliente'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
